import { useCallback, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { addMarketplaceCartItem } from "../services/marketplaceCart.service";
import StorageService from "../services/StorageService";
import useMarketplaceCartProductIds, {
  markMarketplaceCartProductAdded,
} from "./useMarketplaceCartProductIds";

function resolveProductId(product) {
  return product?.id ?? product?.product_id ?? product?.productId ?? null;
}

function resolveMoq(product) {
  const raw = product?.moq ?? product?.min_order ?? product?.minOrder;
  const parsed = Number(raw);
  if (Number.isFinite(parsed) && parsed > 0) return Math.floor(parsed);
  return 1;
}

function resolveProductName(product) {
  return product?.title || product?.name || product?.product_name || "Item";
}

export default function useMarketplaceAddToCart() {
  const navigate = useNavigate();
  const location = useLocation();
  const [addingId, setAddingId] = useState(null);
  const { isInCart } = useMarketplaceCartProductIds();

  const addToCart = useCallback(
    async (product, event) => {
      event?.preventDefault?.();
      event?.stopPropagation?.();

      const productId = resolveProductId(product);
      if (!productId) return false;

      if (isInCart(productId)) {
        toast.info("This product is already in your cart");
        return false;
      }

      if (!StorageService.getToken()) {
        toast.info("Please sign in to add items to cart");
        navigate("/login", { state: { from: location.pathname } });
        return false;
      }

      setAddingId(productId);
      try {
        await addMarketplaceCartItem(productId, resolveMoq(product));
        markMarketplaceCartProductAdded(productId);
        toast.success(`${resolveProductName(product)} added to cart`);
        return true;
      } catch (err) {
        toast.error(err?.response?.data?.message || err?.message || "Could not add to cart.");
        return false;
      } finally {
        setAddingId(null);
      }
    },
    [isInCart, location.pathname, navigate],
  );

  const isAdding = useCallback((productId) => addingId === productId, [addingId]);

  return { addToCart, isAdding, isInCart, addingId };
}
